import { FC, memo, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { match } from "ts-pattern";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import {
  COMPANY_TYPES,
  CompanyTypeOption,
  DEFAULT_ENTITY_TYPE_FILTER_VALUE,
} from "@/components/filters/CompanyType/constants";
import { getCompanyTypeOption } from "@/components/filters/CompanyType/components/utils";

export type CompanyTypeSlice = {
  option: CompanyTypeOption;
};

export const defaultCompanyTypeSlice: CompanyTypeSlice = {
  option: CompanyTypeOption.ALL,
};

const CompanyTypeFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("companyType");
  const { setSlice } = useCompanyFilterActions();

  const selectedOption = useMemo(
    () => slice.option ?? CompanyTypeOption.ALL,
    [slice.option]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSlice("companyType", {
        option: event.target.value as CompanyTypeOption,
      });
    },
    [setSlice]
  );

  return (
    <Box id="company-type-filter-options">
      <RadioGroup value={selectedOption} onChange={handleChange}>
        {COMPANY_TYPES.map((companyType) => (
          <FormControlLabel
            key={companyType.value}
            value={companyType.value}
            id={`entity-type-option-${companyType.label}`}
            control={<Radio size="small" />}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">{companyType.label}</Typography>
                <Tooltip title={companyType.tooltip} arrow>
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
});

CompanyTypeFilter.displayName = "CompanyTypeFilter";

export const companyTypeModule: FilterModule<CompanyTypeSlice> = {
  key: "companyType",
  defaultSlice: defaultCompanyTypeSlice,
  Component: CompanyTypeFilter,
  toCompanyFilters: (slice) => {
    const value = match(slice.option)
      .with(CompanyTypeOption.GHQ, () => ({
        ...DEFAULT_ENTITY_TYPE_FILTER_VALUE,
        isGhq: true,
      }))
      .with(CompanyTypeOption.CP, () => ({
        ...DEFAULT_ENTITY_TYPE_FILTER_VALUE,
        isGp: true,
      }))
      .with(CompanyTypeOption.DP, () => ({
        ...DEFAULT_ENTITY_TYPE_FILTER_VALUE,
        isDp: true,
      }))
      .otherwise(() => ({ ...DEFAULT_ENTITY_TYPE_FILTER_VALUE }));

    const out: Partial<CompanyFilters> = {};
    if (value.isGhq) out.isGhq = true;
    if (value.isGp) out.isGp = true;
    if (value.isDp) out.isDp = true;
    return out;
  },
  fromCompanyFilters: (filters) => {
    const option = getCompanyTypeOption({
      ...DEFAULT_ENTITY_TYPE_FILTER_VALUE,
      isGhq: filters.isGhq ?? undefined,
      isGp: filters.isGp ?? undefined,
      isDp: filters.isDp ?? undefined,
    });
    return { option };
  },
  getChips: (slice, setSlice) => {
    if (!slice.option || slice.option === CompanyTypeOption.ALL) return [];

    const label = match(slice.option)
      .with(CompanyTypeOption.GHQ, () => "Global Headquarters (GHQ)")
      .with(CompanyTypeOption.CP, () => "Corporate Parent")
      .with(CompanyTypeOption.DP, () => "Domestic Parent")
      .otherwise(() => "Company Type");

    return [
      {
        id: "companyType",
        label: `Company Type: ${label}`,
        filterKey: "companyType" as const,
        onRemove: () => setSlice({ option: CompanyTypeOption.ALL }),
      },
    ];
  },
  getCount: (slice) => {
    return slice.option && slice.option !== CompanyTypeOption.ALL ? 1 : 0;
  },
};
